export const SafetyKnowledgeStatus=Object.freeze({
  DRAFT:'draft',
  VALIDATED:'validated',
  REJECTED:'rejected',
  RETIRED:'retired'
});
const STATUSES=new Set(Object.values(SafetyKnowledgeStatus));

function required(value,label,max=4000){
  const result=String(value??'').trim();
  if(!result)throw new TypeError(label+' is required');
  if(result.length>max)throw new TypeError(label+' is too long');
  return result;
}
function optional(value,max=4000){
  const result=String(value??'').trim();
  if(!result)return null;
  if(result.length>max)throw new TypeError('Text is too long');
  return result;
}
function positiveInteger(value,label){
  const number=Number(value);
  if(!Number.isInteger(number)||number<1)throw new TypeError(label+' must be a positive integer');
  return number;
}
function timestamp(value,label,{optionalValue=false}={}){
  if((value==null||value==='')&&optionalValue)return null;
  const raw=required(value,label,100);
  const parsed=Date.parse(raw);
  if(Number.isNaN(parsed))throw new TypeError(label+' must be a timestamp');
  return new Date(parsed).toISOString();
}
function status(value){
  const key=String(value??'').trim().toLowerCase();
  if(!STATUSES.has(key))throw new TypeError('Invalid safety knowledge status');
  return key;
}
function tags(values=[]){
  if(!Array.isArray(values))throw new TypeError('topics must be an array');
  return Object.freeze([...new Set(values.map(value=>required(value,'topic',80).toLowerCase()))].slice(0,20));
}
function sourceMetadata(source={}){
  return Object.freeze({
    publisher:required(source.publisher,'source.publisher',200),
    title:required(source.title,'source.title',500),
    reference:required(source.reference,'source.reference',1000),
    publishedAt:source.publishedAt?timestamp(source.publishedAt,'source.publishedAt'):null,
    accessedAt:timestamp(source.accessedAt,'source.accessedAt')
  });
}

export function createSafetyKnowledgeEntry({
  id,
  version,
  status:entryStatus='draft',
  topics:entryTopics=[],
  statement,
  source,
  reviewNotes=null,
  reviewedBy=null,
  reviewedAt=null,
  createdAt=new Date().toISOString(),
  retiredAt=null
}={}){
  const currentStatus=status(entryStatus);
  const reviewer=optional(reviewedBy,200);
  const reviewTime=reviewedAt?timestamp(reviewedAt,'reviewedAt'):null;
  if(currentStatus===SafetyKnowledgeStatus.VALIDATED&&(!reviewer||!reviewTime)){
    throw new TypeError('Validated safety knowledge requires reviewer and reviewedAt');
  }
  const retired=retiredAt?timestamp(retiredAt,'retiredAt'):null;
  if(currentStatus===SafetyKnowledgeStatus.RETIRED&&!retired){
    throw new TypeError('Retired safety knowledge requires retiredAt');
  }
  return Object.freeze({
    id:required(id,'id',128),
    version:positiveInteger(version,'version'),
    status:currentStatus,
    topics:tags(entryTopics),
    statement:required(statement,'statement',6000),
    source:sourceMetadata(source),
    reviewNotes:optional(reviewNotes,2000),
    reviewedBy:reviewer,
    reviewedAt:reviewTime,
    createdAt:timestamp(createdAt,'createdAt'),
    retiredAt:retired
  });
}

export function retireSafetyKnowledgeEntry(entry,{
  retiredAt=new Date().toISOString(),
  reviewNotes=entry?.reviewNotes
}={}){
  const current=createSafetyKnowledgeEntry(entry);
  return createSafetyKnowledgeEntry({
    ...current,
    status:SafetyKnowledgeStatus.RETIRED,
    retiredAt,
    reviewNotes
  });
}

export function createSafetyKnowledgeIndex(entries=[]){
  if(!Array.isArray(entries))throw new TypeError('entries must be an array');
  const normalized=entries.map(createSafetyKnowledgeEntry);
  function retrieve({topics:requestedTopics=[]}={}){
    const wanted=new Set(tags(requestedTopics));
    const latestValidated=new Map();
    for(const entry of normalized){
      if(entry.status!==SafetyKnowledgeStatus.VALIDATED||entry.retiredAt)continue;
      if(wanted.size>0&&!entry.topics.some(topic=>wanted.has(topic)))continue;
      const current=latestValidated.get(entry.id);
      if(!current||entry.version>current.version)latestValidated.set(entry.id,entry);
    }
    return Object.freeze([...latestValidated.values()]
      .sort((a,b)=>a.id.localeCompare(b.id))
    );
  }

  return Object.freeze({
    retrieve,
    allVersions:Object.freeze([...normalized].sort((a,b)=>
      a.id.localeCompare(b.id)||a.version-b.version
    ))
  });
}

export function safetyKnowledgeFallback({
  reason='validated_knowledge_unavailable'
}={}){
  return Object.freeze({
    kind:'safety_fallback',
    reason:required(reason,'reason',120),
    messageKey:'dymek.safety.validated_knowledge_required'
  });
}
